import { useHistory } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import styled from "styled-components";
import { FormErrorData, Result } from "../../../../api-client/APIClient";
import ErrorFallback from "../../../shared/ErrorFallback/ErrorFallback";
import useProductRestrictions from "../../../shared/hooks/useProductRestrictions";
import CommonModelScopes from "./common-model-scopes/CommonModelScopes";
import { useCommonModelsForCategory } from "./common-model-scopes/hooks/useCommonModelsForCategory";
import { ModelWithSortedFields } from "./CommonModelUtils";
import useAppContext from "../../../context/useAppContext";
import { showErrorToast } from "../../../shared/Toasts";
import { APICategory, UserType } from "../../../../models/Entities";

type ConfigurationCommonModelsPageProps = {
  category: APICategory;
};

const MinWidthCol = styled(Col)`
  min-width: 300px;
`;

export const processErrorResult = async (
  result: Result<void>,
  commonModel: ModelWithSortedFields,
) => {
  if (result.status === "error") {
    let isGenericError = !result.error;
    if (result.error) {
      if (result.error.status === 403) {
        showErrorToast(
          commonModel.is_permanently_disabled
            ? "Common model is permanently disabled and cannot be re-enabled"
            : "You do not have permission to perform this action",
        );
      } else {
        const data: FormErrorData = await result.error.json();
        if (data.non_field_errors) {
          showErrorToast(data.non_field_errors[0]);
        } else {
          isGenericError = true;
        }
      }
    }
    if (isGenericError) {
      showErrorToast("Something went wrong, please check your connection and try again.");
    }
  }
};

/**
 * Shows the Common Models under an API category and allows the user to enable/disable the syncing
 * of that particular model.
 */
const ConfigurationCommonModelsPage = ({ category }: ConfigurationCommonModelsPageProps) => {
  const [isViewing, setIsViewing] = useState(true);

  const {
    models: commonModels,
    setModels: setCommonModels,
    isLoading,
    hasError,
  } = useCommonModelsForCategory(category);
  const { productRestrictions } = useProductRestrictions();
  const { user } = useAppContext();
  const history = useHistory();

  const urlParams = new URLSearchParams(window.location.search);
  const areOptionalScopesEnabled = user?.are_end_user_configurable_scopes_enabled || false;
  const canToggleFields = !!productRestrictions?.are_toggles_enabled;
  // Remote Data / Remote Fields unlock with either the field-toggle or remote-data entitlement.
  const canToggleRemoteData =
    canToggleFields || !!productRestrictions?.are_remote_data_toggles_enabled;
  const isUserAdminWithBilling = user.type === UserType.admin_with_billing;
  const disableOrgScopeEditingFlag = user.is_org_scope_editing_disabled;

  useEffect(() => {
    const hasEditingParam = urlParams.has("isEditing");
    if (hasEditingParam && isUserAdminWithBilling && !disableOrgScopeEditingFlag) {
      setIsViewing(false);
    } else {
      setIsViewing(true);
    }
  }, [isUserAdminWithBilling, disableOrgScopeEditingFlag]);

  const onSwitch = (selectedViewing: boolean) => {
    if (!isUserAdminWithBilling) {
      return;
    }
    // Prevent entering editing mode if org scope editing is disabled
    if (!selectedViewing && disableOrgScopeEditingFlag) {
      return;
    }
    if (selectedViewing) {
      history.push({
        search: "",
      });
      setIsViewing(true);
    } else {
      history.push({
        search: "isEditing=true",
      });
      setIsViewing(false);
    }
  };

  return (
    <Row>
      <MinWidthCol>
        {hasError ? (
          <ErrorFallback />
        ) : (
          <CommonModelScopes
            models={commonModels}
            setModels={setCommonModels}
            category={category}
            isLoading={isLoading}
            isViewing={isViewing || !!disableOrgScopeEditingFlag}
            areOptionalScopesEnabled={areOptionalScopesEnabled}
            canToggleFields={canToggleFields}
            canToggleRemoteData={canToggleRemoteData}
            canEnterEdit={isUserAdminWithBilling && !disableOrgScopeEditingFlag}
            disableOrgScopeEditingFlag={!!disableOrgScopeEditingFlag}
            onEnterEdit={() => onSwitch(false)}
            onExitEdit={() => onSwitch(true)}
          />
        )}
      </MinWidthCol>
    </Row>
  );
};

export default ConfigurationCommonModelsPage;
